import React from 'react';
import PropTypes from 'prop-types';
import update from 'immutability-helper';
import { getSectionRegion, mergeSections } from 'shared/helpers/pageLayout';
import { HOME_PAGE } from 'shared/pageTypes';
import { PREHEADER, HEADER, BODY, FOOTER, FIXED_TOP, FIXED_BOTTOM } from 'utils/sectionTypes';
import { SHOW_UP_ARROW, SHOW_DOWN_ARROW, SHOW_BOTH_ARROWS, SHOW_NO_ARROWS } from 'utils/displaySwapArrows';
import pageTypes from 'utils/pageTypes';
import sectionInflater from 'components/sections/inflater';
import AdminLayoutBuilderButton from '../../../../../admin/containers/AdminLayoutBuilderButton';
import SectionDropZone from './SectionDropZone';
import SectionLoader from './SectionLoader';
import styles from './SectionList.scss';
import StoreContext from '../../../../../modules/react/components/stateful/StoreProvide';

const lockedRegions = [FOOTER, HEADER, PREHEADER, FIXED_TOP, FIXED_BOTTOM];
const emptyPayload = {};

export default class SectionList extends React.Component {
  static propTypes = {
    sections: PropTypes.arrayOf(PropTypes.shape({
      type: PropTypes.string.isRequired,
    })).isRequired,
    layout: PropTypes.shape({
      id: PropTypes.string.isRequired,
      publishedVersion: PropTypes.shape({
        sections: PropTypes.array.isRequired,
      }),
    }),
    pageType: PropTypes.string.isRequired,
    showPlaceholders: PropTypes.bool.isRequired,
    isSectionBeingEdited: PropTypes.func.isRequired,
    isWidgetBeingEdited: PropTypes.func.isRequired,
    onWidgetEditStart: PropTypes.func.isRequired,
    onWidgetEditStop: PropTypes.func.isRequired,
    onSectionEditStart: PropTypes.func.isRequired,
    onSectionEditStop: PropTypes.func.isRequired,
    onUpdate: PropTypes.func.isRequired,
    onShowPopupModal: PropTypes.func,
    onHidePopupModal: PropTypes.func,
    sectionWidgetMoving: PropTypes.bool.isRequired,
    onStartWidgetSwitch: PropTypes.func.isRequired,
    facilityId: PropTypes.string,
    locale: PropTypes.string,
    isSubmitting: PropTypes.bool,
    legacyWidgetLoader: PropTypes.bool,
  };

  static contextType = StoreContext;

  constructor(props) {
    super(props);

    // Build this data up-front to improve drag'n'drop performance
    this.buildSections();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    // Rebuild the section data if we need to
    if (this.props.sections !== nextProps.sections
      || this.props.layout !== nextProps.layout) {
      this.buildSections(nextProps);
    }
  }

  getPageType() {
    return pageTypes[this.props.pageType];
  }

  buildSections({ sections, layout } = this.props) {
    const { pageType } = this.props;
    let pageSections = sections;

    if (pageType === HOME_PAGE) {
      pageSections = sections.filter(section => !section.type.includes('Breadcrumbs'));
    }

    // If this page has an associated layout, add the layout sections into the section list
    if (layout && layout.publishedVersion) {
      let layoutSections = layout.publishedVersion.sections;

      if (pageType === HOME_PAGE) {
        layoutSections = layoutSections.filter(section => !section.type.includes('Breadcrumbs'));
      }

      this.sections = mergeSections(pageSections, layoutSections);
      this.pageSections = this.sections.filter(({ isLayout }) => !isLayout);
      this.sectionStart = layoutSections.findIndex(({ type }) => type === 'ChildBlock');
      this.sectionEnd = this.sectionStart + pageSections.length;
    } else {
      this.sections = pageSections;
      this.pageSections = pageSections;
      this.sectionStart = 0;
      this.sectionEnd = Infinity;
    }

    // Count the regions
    this.regionCounts = this.sections.reduce((counts, section) => {
      const region = getSectionRegion(section.type);
      counts[region]++;
      return counts;
    }, {
      [PREHEADER]: 0,
      [HEADER]: 0,
      [BODY]: 0,
      [FOOTER]: 0,
    });
  }

  reviseSection(id, reviser) {
    const { onUpdate } = this.props;

    onUpdate(this.pageSections.map((section) => {
      if (section.id === id) {
        return reviser(section);
      }

      return section;
    }));
  }

  handleWidgetChange = (sectionId, widgets) => {
    this.reviseSection(sectionId, section => ({
      ...section, widgets,
    }));
  };

  inflateSection(type) {
    return sectionInflater(type, this.context);
  }

  handleReplace = (sectionId, { type }) => {
    const section = this.inflateSection(type);

    this.reviseSection(sectionId, () => ({
      ...section,
    }));
  };

  handleInsert = index => ({ type }) => {
    const { onUpdate } = this.props;
    const section = this.inflateSection(type);

    onUpdate(update(this.pageSections, {
      $splice: [
        [index, 0, section],
      ],
    }));
  }

  handleFinishEdit = (sectionId, payload) => {
    this.reviseSection(sectionId, section => ({
      ...section,
      payload,
    }));
    this.props.onSectionEditStop(sectionId);
  };

  handleCancelEdit = sectionId => this.props.onSectionEditStop(sectionId);

  handleDelete = (sectionId) => {
    const { onUpdate } = this.props;

    onUpdate(this.pageSections.filter(({ id }) => id !== sectionId));
  };

  invalidSwapRegion = (index) => {
    const { sections } = this.props;
    // if there is no region above or below current region, there is nothing to swap with
    if (!sections[index]) { return true; }

    return lockedRegions.indexOf(getSectionRegion(sections[index].type)) !== -1;
  }

  preventSectionSwap = (srcSection, destSection) => {
    const srcRegion = lockedRegions.indexOf(getSectionRegion(srcSection.type)) !== -1;
    const destRegion = lockedRegions.indexOf(getSectionRegion(destSection.type)) !== -1;

    return srcRegion || destRegion;
  }

  showSwapArrows = (sectionId) => {
    const currIndex = this.pageSections.findIndex(section => section.id === sectionId);
    const indexAbove = currIndex - 1;
    const indexBelow = currIndex + 1;
    const sectionLength = this.props.sections.length;
    // check if region is header or footer or length is less than 1
    if (this.invalidSwapRegion(currIndex) || sectionLength <= 1) {
      return SHOW_NO_ARROWS;
    }
    // check if current section is sandwiched between 2 non swappable sections
    if (this.invalidSwapRegion(indexAbove) && this.invalidSwapRegion(indexBelow)) {
      return SHOW_NO_ARROWS;
    }
    // check for locked region above and swappable region below
    if ((!this.invalidSwapRegion(indexBelow) && this.invalidSwapRegion(indexAbove))) {
      return SHOW_DOWN_ARROW;
    }
    // check for locked region below and swappable region above
    if ((this.invalidSwapRegion(indexBelow) && !this.invalidSwapRegion(indexAbove))) {
      return SHOW_UP_ARROW;
    }
    return SHOW_BOTH_ARROWS;
  }

  handleSwapUp = (sectionId) => {
    const { sections } = this.props;
    const srcIndex = this.pageSections.findIndex(section => section.id === sectionId);
    const destIndex = srcIndex - 1;
    const cantSwap = this.preventSectionSwap(sections[srcIndex], sections[destIndex]);
    if (destIndex < 0 || cantSwap) {
      return;
    }
    this.swap(srcIndex, destIndex);
  }

  handleSwapDown = (sectionId) => {
    const { sections } = this.props;
    const srcIndex = this.pageSections.findIndex(section => section.id === sectionId);
    const destIndex = srcIndex + 1;
    const cantSwap = this.preventSectionSwap(sections[srcIndex], sections[destIndex]);

    if (destIndex > this.pageSections.length - 1 || cantSwap) {
      return;
    }
    this.swap(srcIndex, destIndex);
  }

  swap = (srcIndex, destIndex) => {
    const to = this.pageSections[destIndex];
    const from = this.pageSections[srcIndex];

    this.pageSections = Object.assign(
      [],
      this.pageSections,
      {
        [destIndex]: from,
        [srcIndex]: to,
      },
    );

    this.props.onUpdate(this.pageSections);
  }

  locateWidget = (widgetId) => {
    const sectionIdx = this.pageSections.findIndex(section =>
      section.widgets.find(widget => widget && widget.id === widgetId));
    const widgetIdx = this.pageSections[sectionIdx].widgets.findIndex(widget => widget && widget.id === widgetId);
    return [sectionIdx, widgetIdx];
  }

  handleWidgetSwitch = (widgetId1, widgetId2) => {
    const [sectionOneIndex, widgetOneIndex] = this.locateWidget(widgetId1);
    const [sectionTwoIndex, widgetTwoIndex] = this.locateWidget(widgetId2);

    const widgetOne = {
      ...this.pageSections[sectionOneIndex].widgets[widgetOneIndex],
    };

    const widgetTwo = {
      ...this.pageSections[sectionTwoIndex].widgets[widgetTwoIndex],
    };

    if (sectionOneIndex === sectionTwoIndex) {
      const section = {
        ...this.pageSections[sectionOneIndex],
        widgets: Object.assign(
          [],
          this.pageSections[sectionOneIndex].widgets,
          {
            [widgetOneIndex]: widgetTwo,
            [widgetTwoIndex]: widgetOne,
          },
        ),
      };

      const pageSections = Object.assign(
        [],
        this.pageSections,
        {
          [sectionOneIndex]: section,
        },
      );

      this.props.onUpdate(pageSections);
      return;
    }

    const sectionOne = {
      ...this.pageSections[sectionOneIndex],
      widgets: Object.assign(
        [],
        this.pageSections[sectionOneIndex].widgets,
        { [widgetOneIndex]: widgetTwo },
      ),
    };

    const sectionTwo = {
      ...this.pageSections[sectionTwoIndex],
      widgets: Object.assign(
        [],
        this.pageSections[sectionTwoIndex].widgets,
        { [widgetTwoIndex]: widgetOne },
      ),
    };

    const pageSections = Object.assign(
      [],
      this.pageSections,
      {
        [sectionOneIndex]: sectionOne,
        [sectionTwoIndex]: sectionTwo,
      },
    );

    this.props.onUpdate(pageSections);
  }

  handleInsertLast = ({ type }) => {
    const { onUpdate } = this.props;
    const section = this.inflateSection(type);

    onUpdate([...this.pageSections, section]);
  };

  /**
   * This function is probably too complex; a likely candidate for refactoring.
   */
  handleCanDrop = (isPlaceholder, sectionIndex) => (regions, { type }) => {
    // If the section index lives in the page layout's region, reject immediately
    if (sectionIndex < this.sectionStart || sectionIndex > this.sectionEnd) {
      return false;
    }

    const pageType = this.getPageType();
    const region = getSectionRegion(type);

    // If this drop zone can't even take this section, bail
    if (regions.indexOf(region) === -1) {
      return false;
    }

    // Ensure this type is on the whitelist or not on the blacklist
    if (!pageType[region].accepts(type)) {
      return false;
    }

    // Ensure we're below the page region limit if this is a placeholder or another type
    const isNewSectionType = isPlaceholder || getSectionRegion(this.sections[sectionIndex].type) !== region;
    if (isNewSectionType && pageType[region].limit && pageType[region].limit <= this.regionCounts[region]) {
      return false;
    }

    const weightAtIndex = (index, fallback = 0) => {
      let weight = fallback;

      const candidate = this.sections[index];
      if (candidate && getSectionRegion(candidate.type) === region) {
        weight = pageType[region].weight(candidate.type);
      }

      return weight;
    };
    const preWeight = weightAtIndex(sectionIndex - 1, Infinity);
    const postWeight = weightAtIndex(sectionIndex + (isPlaceholder ? 0 : 1), -Infinity);
    const weight = pageType[region].weight(type);

    return preWeight >= weight && weight >= postWeight;
  }

  handleCanDropOnExisting = (...args) => this.handleCanDrop(false, ...args);
  handleCanDropOnPlaceholder = (...args) => this.handleCanDrop(true, ...args);

  addSectionPlaceholders(sectionComponents) {
    const components = [];
    const remainingRegions = [PREHEADER, HEADER, BODY, FOOTER];

    // Sprinkle drop zones between each section component
    sectionComponents.forEach((component, index) => {
      const region = getSectionRegion(this.sections[index].type);
      const acceptedRegions = [region];
      if (remainingRegions.indexOf(region) !== -1) {
        while (remainingRegions[0] !== region) {
          acceptedRegions.push(remainingRegions.shift());
        }
      }

      components.push(
        <SectionDropZone
          key={`dropzone-${index}`}
          onCanDrop={this.handleCanDrop(true, index)}
          onDrop={this.handleInsert(index - this.sectionStart)}
          accepts={acceptedRegions}
        />,
      );
      components.push(component);
    });

    // Add a final drop zone at the end with the remaining regions
    components.push(
      <SectionDropZone
        key="dropzone-last"
        onCanDrop={this.handleCanDrop(true, sectionComponents.length)}
        onDrop={this.handleInsertLast}
        accepts={remainingRegions}
      />,
    );

    return components;
  }

  renderSection = (section, index) => {
    if (section.isLayout) {
      return (
        <AdminLayoutBuilderButton
          key={section.id}
          layoutId={this.props.layout.id}
          data={emptyPayload}
          payload={emptyPayload}
          isEditing={false}
          isEditable={false}
          isBuilderView
          showSwapArrows={this.showSwapArrows}
          {...section}
        />
      );
    }

    const {
      isSectionBeingEdited,
      isWidgetBeingEdited,
      onWidgetEditStart,
      onWidgetEditStop,
      onSectionEditStart,
      onShowPopupModal,
      onHidePopupModal,
      sectionWidgetMoving,
      onStartWidgetSwitch,
      facilityId,
      locale,
      isSubmitting,
      legacyWidgetLoader,
    } = this.props;

    const editFunctions = {
      start: onSectionEditStart,
      finish: this.handleFinishEdit,
      cancel: this.handleCancelEdit,
      editWidget: onWidgetEditStart,
    };

    if (section.type === 'AddOns') {
      editFunctions.start = onShowPopupModal;
      editFunctions.finish = onHidePopupModal;
      editFunctions.cancel = onHidePopupModal;
      editFunctions.editWidget = onShowPopupModal;
    }

    // section data must at least be an empty object
    const sectionProps = {
      position: index,
      data: emptyPayload,
      payload: emptyPayload,
      ...section,
      isEditing: isSectionBeingEdited(section),
      isWidgetBeingEdited,
      locale,
      onWidgetEditStart: editFunctions.editWidget,
      onWidgetEditStop,
      onEditStart: editFunctions.start,
      onFinishEdit: editFunctions.finish,
      onCancelEdit: editFunctions.cancel,
      onWidgetChange: this.handleWidgetChange,
      onCanDrop: this.handleCanDropOnExisting,
      onDrop: this.handleReplace,
      onDelete: this.handleDelete,
      onSwapUp: this.handleSwapUp,
      onSwapDown: this.handleSwapDown,
      showSwapArrows: this.showSwapArrows,
      sectionWidgetMoving,
      onStartWidgetSwitch,
      onSwitchWidgets: this.handleWidgetSwitch,
      pageFacilityId: facilityId,
      isSubmitting,
      legacyWidgetLoader,
    };
    return (
      <SectionLoader
        {...sectionProps}
        key={section.id}
      />
    );
  };

  render() {
    const sectionComponents = this.sections.map(this.renderSection);

    return (
      <div className={styles.sectionList}>
        {this.addSectionPlaceholders(sectionComponents)}
      </div>
    );
  }
}
